// Cola de sincronización de aplicaciones de fertilizantes creadas offline.
// Mismo patrón que services/offline/syncQueue.js (Aplicaciones), con su
// propio store IndexedDB (offlineDbFertilizantes.js) — nunca se mezclan
// registros fitosanitarios con fertilizantes.
import {
  listPendingAplicacionesFertilizantesByUser,
  updatePendingAplicacionFertilizanteStatus,
  removePendingAplicacionFertilizante,
  addPendingAplicacionFertilizante,
} from './offlineDbFertilizantes';
import { crearAplicacionFertilizante } from '../aplicacionesFertilizantes';
import { isOnline } from './networkStatus';

let syncing = false;

function notifyChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('fg-offline-fertilizantes-change'));
  }
}

function generateClientOperationId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Punto de entrada único para "Confirmar aplicación" en el formulario de
// Nueva aplicación de fertilizantes: decide si crea online o encola
// offline, igual que createAplicacionOfflineFirst para Aplicaciones.
export async function createAplicacionFertilizanteOfflineFirst(authUserId, payload) {
  const clientOperationId = generateClientOperationId();

  const saveLocally = async () => {
    await addPendingAplicacionFertilizante({
      client_operation_id: clientOperationId,
      auth_user_id: authUserId,
      payload,
      estado_local: 'PENDIENTE',
      error_message: null,
      created_at_local: new Date().toISOString(),
    });
    notifyChange();
    return { ok: true, offline: true, clientOperationId };
  };

  if (!isOnline()) {
    return saveLocally();
  }

  const result = await crearAplicacionFertilizante({ ...payload, client_operation_id: clientOperationId });
  if (result.ok) {
    return { ok: true, offline: false, id: result.id };
  }
  if (result.networkError) {
    return saveLocally();
  }
  return result;
}

export function isSyncingFertilizantes() {
  return syncing;
}

export async function trySyncFertilizantes(authUserId) {
  if (!authUserId || syncing || !isOnline()) {
    return { attempted: 0, synced: 0 };
  }

  syncing = true;
  notifyChange();
  let attempted = 0;
  let synced = 0;

  try {
    const pending = await listPendingAplicacionesFertilizantesByUser(authUserId);
    const toSync = pending.filter((item) => item.estado_local === 'PENDIENTE' || item.estado_local === 'ERROR');

    for (const item of toSync) {
      attempted += 1;
      await updatePendingAplicacionFertilizanteStatus(item.client_operation_id, {
        estado_local: 'SINCRONIZANDO',
        error_message: null,
      });
      notifyChange();

      const result = await crearAplicacionFertilizante({
        ...item.payload,
        client_operation_id: item.client_operation_id,
      });

      if (result.ok) {
        await removePendingAplicacionFertilizante(item.client_operation_id);
        synced += 1;
      } else {
        await updatePendingAplicacionFertilizanteStatus(item.client_operation_id, {
          estado_local: 'ERROR',
          error_message: result.message,
        });
      }
      notifyChange();
    }
  } finally {
    syncing = false;
    notifyChange();
  }

  return { attempted, synced };
}
